import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Download, Eye, Ban, Send, X } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { TicketPass } from "../../components/ui/TicketPass";
import { exportCSV } from "../../lib/csv";
import { formatIDR } from "../../lib/format";
import { TICKET_PACKAGE } from "../../data/mockData";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

export default function AdminTickets() {
  const { tickets, cancelTicket } = useApp();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState(null);

  const filtered = useMemo(() => tickets.filter((t) => {
    if (statusFilter !== "all" && t.paymentStatus !== statusFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      return t.ticketCode.toLowerCase().includes(s) || t.buyerName.toLowerCase().includes(s) ||
        t.email.toLowerCase().includes(s) || t.mobile.includes(s) || t.orderNumber.toLowerCase().includes(s);
    }
    return true;
  }), [tickets, q, statusFilter]);

  const doExport = () => {
    exportCSV("tickets.csv", filtered.map((t) => ({
      TicketCode: t.ticketCode, OrderID: t.orderNumber, Buyer: t.buyerName, Parent: t.parentName,
      Child: t.childName, Email: t.email, Mobile: t.mobile, PurchaseDate: t.purchaseDate,
      PaymentStatus: t.paymentStatus, TicketStatus: t.ticketStatus,
      ActivitiesBooked: t.bookings.filter((b) => b.status === "Confirmed").length,
    })));
    toast.success("Daftar tiket diekspor ke CSV.");
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-slate-800">Ticket Management</h1>
          <p className="text-slate-500 font-semibold">{filtered.length} tiket</p>
        </div>
        <button onClick={doExport} className="flex items-center gap-2 bg-grass text-white font-display rounded-lg px-4 py-2.5" data-testid="export-tickets">
          <Download className="w-5 h-5" /> Export CSV
        </button>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode, nama, order, email..."
            data-testid="tickets-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 focus:border-sea outline-none font-semibold text-slate-700" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2.5 font-semibold text-slate-600" data-testid="tickets-filter">
          <option value="all">Semua Status</option>
          <option>Paid</option>
          <option>Pending</option>
        </select>
      </div>

      <div className="mt-5 bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto thin-scroll">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 text-slate-500 font-bold text-left">
            <tr>
              {["Ticket", "Order", "Buyer", "Child", "Mobile", "Date", "Payment", "Activities", "Aksi"].map((h) => (
                <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((t) => (
              <tr key={t.ticketCode} className="hover:bg-slate-50/60" data-testid={`ticket-row-${t.ticketCode}`}>
                <td className="px-4 py-3 font-display text-coral">{t.ticketCode}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{t.orderNumber}</td>
                <td className="px-4 py-3 font-semibold text-slate-700">{t.buyerName}</td>
                <td className="px-4 py-3 text-slate-600">{t.childName}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{t.mobile}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{t.purchaseDate}</td>
                <td className="px-4 py-3"><StatusBadge status={t.paymentStatus} /></td>
                <td className="px-4 py-3 text-slate-600">{t.bookings.filter((b) => b.status === "Confirmed").length}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <IconBtn onClick={() => setView(t)} title="Lihat" testid={`view-${t.ticketCode}`}><Eye className="w-4 h-4" /></IconBtn>
                    <IconBtn onClick={() => toast.success("Info tiket dikirim ulang (prototype).")} title="Resend" testid={`resend-${t.ticketCode}`}><Send className="w-4 h-4" /></IconBtn>
                    <IconBtn danger disabled={t.ticketStatus === "Cancelled"} onClick={() => { cancelTicket(t.ticketCode); toast.success("Tiket dibatalkan."); }} title="Batalkan" testid={`cancel-${t.ticketCode}`}><Ban className="w-4 h-4" /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400 font-semibold">Tidak ada tiket.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-slate-800">Detail Tiket</DialogTitle>
          </DialogHeader>
          {view && (
            <div>
              <TicketPass ticket={view} />
              <div className="mt-4 text-sm">
                <p className="font-bold text-slate-600">Aktivitas Terbooking:</p>
                <p className="text-slate-500">{view.bookings.filter((b) => b.status === "Confirmed").length} aktivitas · Total bayar {formatIDR(TICKET_PACKAGE.price)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const IconBtn = ({ children, onClick, title, danger, disabled, testid }) => (
  <button
    onClick={onClick} title={title} disabled={disabled} data-testid={testid}
    className={`p-2 rounded-lg border ${danger ? "border-destructive/30 text-destructive hover:bg-destructive/10" : "border-slate-200 text-slate-500 hover:bg-slate-50"} disabled:opacity-40`}
  >
    {children}
  </button>
);
